import { useCallback, useEffect, useMemo, useState } from "react";

import type { FiltersData, FiltersFlags } from "app/pages/activation/components/ProjectsList/ProjectListHeader/types";
import { ProjectsFilters } from "app/pages/activation/components/ProjectsList/ProjectListHeader/types";
import { activationTracking } from "app/pages/activation/services/tracking";
import type { ActivationSetSchema } from "app/pages/activation/types/api";
import CountryService from "app/services/CountryService";

export const useFilterData = (data: ActivationSetSchema[]) => {
    const [filteredData, setFilteredData] = useState<ActivationSetSchema[]>([]);
    const [activeFilter, setActiveFilter] = useState<ProjectsFilters>(ProjectsFilters.All);
    const [searchValue, setSearchValue] = useState("");

    const track = activationTracking.homepage;

    const filtersData = useMemo(() => {
        const filtered: FiltersData = {
            [ProjectsFilters.All]: [...data],
            [ProjectsFilters.My]: [],
            [ProjectsFilters.Inc]: [],
            [ProjectsFilters.Dec]: [],
            [ProjectsFilters.NewAlert]: [],
        };

        for (const item of data) {
            const { isOwn, periodOverPeriodShareDifference, hasNewAlerts } = item;

            if (isOwn) {
                filtered[ProjectsFilters.My].push(item);
            }
            if (periodOverPeriodShareDifference > 0) {
                filtered[ProjectsFilters.Inc].push(item);
            } else {
                filtered[ProjectsFilters.Dec].push(item);
            }
            if (hasNewAlerts) {
                filtered[ProjectsFilters.NewAlert].push(item);
            }
        }

        return filtered;
    }, [data]);

    const filtersFlags = Object.keys(filtersData).reduce((acc, key) => {
        acc[key as ProjectsFilters] = false;

        return acc;
    }, {} as FiltersFlags);

    const onSearch = useCallback(
        (text: string) => {
            const searchResult: ActivationSetSchema[] = filtersData[activeFilter].filter((item) => {
                const { country, name, primaryDomain, competitorsDomains } = item;
                const countryText = CountryService.getCountryById(country)?.text;

                const predicate = text.toLowerCase();
                const searchList = [
                    name?.toLocaleLowerCase(),
                    countryText?.toLowerCase(),
                    primaryDomain?.toLowerCase(),
                    ...competitorsDomains.map((c) => c.toLowerCase()),
                ];

                return searchList.some((sl) => sl?.includes(predicate));
            });

            track.trackProjectSearch(text, searchResult.length);

            setFilteredData(searchResult);
            setSearchValue(text);
        },
        [activeFilter, filtersData, track],
    );

    const onFilter = useCallback(
        (filter: ProjectsFilters) => {
            const filterData = filtersData[filter] ?? [];
            setActiveFilter(filter);
            track.trackFilterApply(filter);

            if (searchValue) {
                onSearch(searchValue);

                return;
            }

            setFilteredData(filterData);
        },
        [filtersData, onSearch, searchValue, track],
    );

    useEffect(() => {
        if (data) {
            onFilter(activeFilter);
        }
    }, [data, activeFilter, onFilter]);

    return { filteredData, onSearch, onFilter, filtersFlags, activeFilter };
};
